import { useState, useEffect } from 'react'
import { sub } from 'date-fns'
import { jwtDecode } from 'jwt-decode'
import HomeStats from '../components/home/HomeStats'
import HomeChart from '../components/home/HomeChart'
import HomeVulnerabilitiesChart from '../components/home/HomeVulnerabilitiesChart'
import HomeDateRangePicker from '../components/home/HomeDateRangePicker'
import { Range } from '../types'
import { useAuthStore } from '../stores/authStore'
import { useDashboardStore } from '../stores/dashboardStore'

interface JWTToken {
  sub: string
  email: string
  tenantId?: string
  roles: string[]
  type: string
  iat: number
  exp: number
  aud: string
  iss: string
}

const Home = () => {
  const getEndOfDay = (date: Date) => {
    const endDate = new Date(date)
    endDate.setHours(23, 59, 59, 999)
    return endDate
  }

  const [range, setRange] = useState<Range>({
    start: sub(new Date(), { days: 30 }),
    end: getEndOfDay(new Date())
  })
  
  const { getUserRole, selectedTenantId } = useAuthStore()
  const { data: dashboardData, loading, fetchDashboardData } = useDashboardStore()

  useEffect(() => {
    const userRole = getUserRole()
    
    // Determine tenantId based on role
    let tenantId: string | undefined
    if (userRole === 'ADMIN') {
      // ADMIN: no tenantId filter
      tenantId = undefined
    } else if (userRole === 'PENTESTER') {
      // PENTESTER: use selected tenant
      tenantId = selectedTenantId || undefined
    } else {
      // TENANT_ADMIN, TENANT_USER: decode JWT token to get tenantId
      try {
        const token = localStorage.getItem('accessToken')
        if (token) {
          const decoded = jwtDecode<JWTToken>(token)
          tenantId = decoded.tenantId
          console.log('Decoded tenantId from JWT:', tenantId)
        }
      } catch (error) {
        console.error('Failed to decode token:', error)
        tenantId = undefined
      }
    }

    console.log('Fetching dashboard data with tenantId:', tenantId)
    fetchDashboardData({
      tenantId,
      startDate: range.start.toISOString(),
      endDate: range.end.toISOString()
    })
  }, [range, selectedTenantId, getUserRole, fetchDashboardData])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Welcome to your dashboard
          </p>
        </div>
        
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <HomeDateRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : dashboardData ? (
        <div className="space-y-6">
          <HomeStats data={dashboardData.overview} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <HomeChart data={dashboardData.vulnerabilities.byStatus} />
            <HomeVulnerabilitiesChart data={dashboardData.vulnerabilities.bySeverity} />
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No data available</p>
        </div>
      )}
    </div>
  )
}

export default Home
